import { useState } from "react";
import { toast } from "react-toastify";
import { buttonVariants } from "~/atoms/button";
import { type User } from "~/api/types";
import { useTranslation } from "react-i18next";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/atoms/alert-dialog";
import { UserStatus } from "~/config/types";
import { updateUser } from "~/api/v2/requests";
import { OffboardWorkflowWarning } from "~/molecules/OffboardWorkflowWarning";

interface Props {
  isOpen: boolean;
  userData?: User;
  onClose: () => void;
  onSuccess: () => void;
}

export const ToggleUserStatusAlert = ({
  isOpen,
  userData,
  onClose,
  onSuccess,
}: Props) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const isActive = userData?.status === UserStatus.active;

  const onConfirm = async () => {
    if (!userData) return;

    try {
      setIsLoading(true);
      const newStatus = isActive ? UserStatus.deactivated : UserStatus.active;
      await updateUser(userData.id, { status: newStatus });
      onSuccess();
      onClose();
    } catch {
      toast.error(t(($) => $.toasts.error.generic));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AlertDialog open={isOpen} onOpenChange={onClose}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {isActive
              ? t(($) => $.settings.userManagement.disableUser)
              : t(($) => $.settings.userManagement.activateUser)}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {isActive
              ? t(($) => $.settings.userManagement.descriptionPartDisable)
              : t(($) => $.settings.userManagement.descriptionPartEnable)}
          </AlertDialogDescription>
        </AlertDialogHeader>
        {isActive && <OffboardWorkflowWarning />}
        <AlertDialogFooter>
          <AlertDialogCancel onClick={onClose}>
            {t(($) => $.commons.buttons.cancel)}
          </AlertDialogCancel>
          <AlertDialogAction
            disabled={isLoading}
            onClick={onConfirm}
            className={buttonVariants({
              variant: isActive ? "destructive" : "default",
            })}
          >
            {t(($) => $.commons.buttons.confirm)}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
